import {
	AttachmentSourceType,
	IMAGE_EXTENSIONS,
	VIDEO_EXTENSIONS,
} from '@shared/constants/file';
import first from 'lodash/first';
import { filetypeinfo } from 'magic-bytes.js';
import { extension as findExtension } from 'mime-types';

import { URL_REGEX } from '@shared/constants/regexes';

type GuessedFile = ReturnType<typeof filetypeinfo>[0];

export const getFileName = (url: string) => {
	return url.substring(url.lastIndexOf('/') + 1);
};

export const getExtension = (name: string) => {
	return name.split('.').pop()?.toLowerCase() ?? '';
};

export const getAttachmentFileType = (url: string): AttachmentSourceType => {
	if (!URL_REGEX.test(url)) return AttachmentSourceType.UNKNOWN;

	const urlParams = new URL(url).searchParams;
	const type = urlParams.get('type');
	const cleanUrl = url.split('?')[0];
	const extension = cleanUrl.split('.').pop()?.toLowerCase();

	if (type) {
		switch (type) {
			case 'image':
				return AttachmentSourceType.IMAGE;
			case 'video':
				return AttachmentSourceType.VIDEO;
			case 'pdf':
				return AttachmentSourceType.PDF;
			default:
				return AttachmentSourceType.UNKNOWN;
		}
	}

	// fallback
	if (extension && IMAGE_EXTENSIONS.includes(extension)) {
		return AttachmentSourceType.IMAGE;
	} else if (extension && VIDEO_EXTENSIONS.includes(extension)) {
		return AttachmentSourceType.VIDEO;
	} else if (extension === AttachmentSourceType.PDF) {
		return AttachmentSourceType.PDF;
	} else {
		return AttachmentSourceType.UNKNOWN;
	}
};

export interface FileDetails {
	name: string;
	size: number;
	type: string;
	extension: string;
	data: string;
	objectURL: string;
	presignedUrl?: string; // Optional presigned URL from backend
}

export const getFileDetails = async (file: File): Promise<FileDetails> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			return resolve({
				name: file.name,
				size: file.size,
				type: file.type,
				extension: file.name.split('.').pop() ?? '',
				data: reader.result as string,
				objectURL: URL.createObjectURL(file),
			});
		};
		reader.readAsDataURL(file);
		reader.onerror = reject;
	});
};

export const getAttachmentFileTypeFromContentType = (
	contentType: string,
	url?: string
): AttachmentSourceType => {
	if (!contentType) return getAttachmentFileType(url ?? '');

	if (contentType.startsWith('image/')) {
		return AttachmentSourceType.IMAGE;
	} else if (contentType.startsWith('video/')) {
		return AttachmentSourceType.VIDEO;
	} else if (contentType.startsWith('application/pdf')) {
		return AttachmentSourceType.PDF;
	} else {
		return AttachmentSourceType.UNKNOWN;
	}
};

export const fileToUint8Array = async (
	file: File | Blob
): Promise<Uint8Array | null> => {
	const arrayBuffer = await fileToArrayBuffer(file);
	return arrayBuffer instanceof ArrayBuffer
		? new Uint8Array(arrayBuffer)
		: null;
};

export const fileToArrayBuffer = async (
	file: File | Blob
): Promise<ArrayBuffer | null> =>
	new Promise((resolve) => {
		const reader = new FileReader();
		reader.readAsArrayBuffer(file);
		reader.onload = (e: any) => resolve(e?.target?.result);
		reader.onerror = () => resolve(null);
	});

export const urlToFile = async (
	url: string,
	fileName: string,
	options?: {
		/**
		 * If true, uses the fileName as-is without appending auto-detected extension.
		 * If false (default), auto-detects file type and appends extension.
		 */
		preserveFileName?: boolean;
		/**
		 * Optional content type to use for the File object.
		 * If not provided, uses the blob's content type.
		 */
		contentType?: string;
	}
): Promise<File> => {
	const blob: Blob = await fetch(url).then((res) => res.blob());

	let finalFileName = fileName;

	// Only auto-detect extension if preserveFileName is false (default behavior)
	if (!options?.preserveFileName) {
		const bytes = await fileToUint8Array(blob);
		const infos: GuessedFile[] =
			bytes instanceof Uint8Array ? filetypeinfo(bytes) : [];
		const ext = first(infos)?.extension;
		finalFileName = `${fileName}${ext ? `.${ext}` : ''}`;
	}

	return new File([blob], finalFileName, {
		type: options?.contentType || blob.type,
	});
};

export const fileToBase64 = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = reject;
	});

export const dataUriToFile = (dataUri: string, fileName: string) => {
	const byteString = atob(dataUri.split(',')[1]);
	const mimeString = dataUri.split(',')[0].split(':')[1].split(';')[0];

	const ab = new ArrayBuffer(byteString.length);
	const ia = new Uint8Array(ab);
	for (let i = 0; i < byteString.length; i++) {
		ia[i] = byteString.charCodeAt(i);
	}

	const blob = new Blob([ab], { type: mimeString });
	return new File([blob], `${fileName}.${findExtension(mimeString)}`);
};

export const getFileSizeWithUnit = (size: number): string => {
	if (size < 1024) {
		return `${size} B`;
	} else if (size < 1024 * 1024) {
		return `${(size / 1024).toFixed(2)} KB`;
	} else if (size < 1024 * 1024 * 1024) {
		return `${(size / (1024 * 1024)).toFixed(2)} MB`;
	} else {
		return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
	}
};
